import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import {
  CarrierQuoteConfig,
  QuickQuoteConfig,
  SchedulerConfig,
  Service,
  ServiceFeatureConfigurations,
  ServiceFeaturesListType,
} from 'contexts/serviceContext';
import { SubTransportTypeOption } from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { IntegrationCore } from 'types/Integration';
import { Maybe } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';
import { EmailProvider } from 'types/enums/EmailProvider';
import { IntegrationSubType } from 'types/enums/Integrations';
import captureException from 'utils/captureException';

type FetchServiceFeaturesResponse = {
  id: number;
  tmsIntegrations: IntegrationCore[];
  quotingIntegrations: IntegrationCore[];
  schedulerIntegrations: IntegrationCore[];
  carrierVerificationIntegrations: IntegrationCore[];
  accessorialIntegrations: IntegrationCore[];
  configurations: ServiceFeatureConfigurations;
  quickQuoteConfig: QuickQuoteConfig;
  subTransportTypes?: SubTransportTypeOption[];
  carrierQuoteConfig?: Maybe<CarrierQuoteConfig>;
  schedulerConfig?: SchedulerConfig;
  scac?: Maybe<string>;
  needsInboxReauthorization: boolean;
  emailProvider: Maybe<EmailProvider>;
  isSharedMailboxUser?: boolean;
} & ServiceFeaturesListType;

export default async function fetchServiceFeatures(
  serviceID: number
): Promise<Result<FetchServiceFeaturesResponse, ApiError>> {
  try {
    const response = await axios.get<FetchServiceFeaturesResponse>(
      `service/${encodeURIComponent(serviceID)}/features`
    );

    return ok(response.data);
  } catch (error) {
    captureException(error, { functionName: 'fetchServiceFeatures' });

    if (!isAxiosError(error)) {
      return err({ message: 'Failed to fetch service features' });
    }

    if (error.response?.status === 404) {
      return err({ message: 'Service not found' });
    }

    return err({
      message:
        error.response?.data?.message || 'Failed to fetch service features',
    });
  }
}

export const convertServiceDataToService = (
  serviceData: FetchServiceFeaturesResponse
): Service => {
  const {
    id,
    tmsIntegrations,
    quotingIntegrations,
    schedulerIntegrations,
    carrierVerificationIntegrations,
    accessorialIntegrations,
    configurations,
    quickQuoteConfig,
    subTransportTypes,
    carrierQuoteConfig,
    schedulerConfig,
    needsInboxReauthorization,
    emailProvider,
    isSharedMailboxUser,
    ...features
  } = serviceData;

  // Since most existing code assumes TMSes to be FTL, this
  // is treated as the default TMS list and LTL TMSes are stored separately.
  const ftlTMSIntegrations = tmsIntegrations
    ? tmsIntegrations.filter(
        (t) =>
          t.subtype === IntegrationSubType.FTL ||
          t.subtype === IntegrationSubType.Hybrid
      )
    : [];

  const ltlTMSIntegrations = tmsIntegrations
    ? tmsIntegrations.filter(
        (t) =>
          t.subtype === IntegrationSubType.LTL ||
          t.subtype === IntegrationSubType.Hybrid
      )
    : [];

  return {
    serviceID: id,
    tmsIntegrations: ftlTMSIntegrations,
    ltlTMSIntegrations,
    quotingIntegrations,
    schedulerIntegrations,
    carrierVerificationIntegrations,
    accessorialIntegrations: accessorialIntegrations ?? [],
    configurations,
    quickQuoteConfig,
    subTransportTypes,
    carrierQuoteConfig,
    schedulerConfig,
    needsInboxReauthorization,
    emailProvider,
    isSharedMailboxUser: isSharedMailboxUser ?? false,
    serviceFeaturesEnabled: features,
  } as Service;
};
